import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CatalogoItem,
  CrearNotificacionInstitucionalRequest,
  EditarNotificacionInstitucionalRequest,
  NotificacionInstitucional,
  NotificacionesFiltros,
  NotificacionesPaginadas,
  SeccionEnlace
} from '../models/notificacion-institucional.model';

@Injectable({ providedIn: 'root' })
export class NotificacionesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrls.notificacionService;
  // Catálogos de provincias/localidades/rangos-etarios viven en
  // profile-person-service, NO en notificacion-service — ver
  // docs/specs/abmc-notificacion-institucional.md §5/§6/§9.1. Cobertura no
  // tiene catálogo en ningún lado.
  private readonly profilePersonUrl = environment.apiUrls.profilePersonService;

  listar(filtros: NotificacionesFiltros): Observable<NotificacionesPaginadas> {
    let params = new HttpParams()
      .set('pagina', filtros.pagina)
      .set('tamanoPagina', filtros.tamanoPagina)
      .set('incluirEliminadas', filtros.incluirEliminadas);
    if (filtros.tipoEntrega) {
      params = params.set('tipoEntrega', filtros.tipoEntrega);
    }
    if (filtros.nombreInterno) {
      params = params.set('nombreInterno', filtros.nombreInterno);
    }
    // El contrato real devuelve { items, hayMas } — sin paginaActual ni
    // tamanoPagina (el frontend los sigue llevando localmente, ya los tiene
    // en _filtros). Se mapea acá al shape interno NotificacionesPaginadas.
    return this.http
      .get<{ items: NotificacionInstitucional[]; hayMas: boolean }>(this.baseUrl, { params })
      .pipe(
        map(res => ({
          contenido: res.items,
          paginaActual: filtros.pagina,
          tamanoPagina: filtros.tamanoPagina,
          hayMas: res.hayMas
        }))
      );
  }

  obtener(id: number): Observable<NotificacionInstitucional> {
    return this.http.get<NotificacionInstitucional>(`${this.baseUrl}/${id}`);
  }

  crear(request: CrearNotificacionInstitucionalRequest): Observable<NotificacionInstitucional> {
    return this.http.post<NotificacionInstitucional>(this.baseUrl, request);
  }

  editar(id: number, request: EditarNotificacionInstitucionalRequest): Observable<NotificacionInstitucional> {
    return this.http.put<NotificacionInstitucional>(`${this.baseUrl}/${id}`, request);
  }

  eliminar(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  // Este catálogo sí vive en notificacion-service (no en profile-person).
  listarSeccionesEnlace(): Observable<SeccionEnlace[]> {
    return this.http.get<SeccionEnlace[]>(`${this.baseUrl}/secciones-enlace`);
  }

  // Rango etario dejó de ser un catálogo (corrección #7 de la spec:
  // rangoEtarioIds → edadDesde/edadHasta) — no se llama más a
  // /rangos-etarios desde esta feature. Ver §5/§9.1.

  // Corrección #9 (docs/specs/abmc-notificacion-institucional.md §9.10):
  // antes devolvía string[] plano (Cobertura "sin id propio", corrección #8)
  // — backend agregó id_objeto al contrato para matchear de forma confiable
  // al editar, igual que Provincia/Localidad.
  listarCoberturas(): Observable<CatalogoItem[]> {
    return this.http.get<CatalogoItem[]>(`${this.profilePersonUrl}/coberturas`);
  }

  listarProvincias(): Observable<CatalogoItem[]> {
    return this.http.get<CatalogoItem[]>(`${this.profilePersonUrl}/provincias`);
  }

  listarLocalidades(provinciaIds: number[]): Observable<CatalogoItem[]> {
    // Sin provinciaIds → [] directo, no se interpreta como "todas" (§5/§6).
    if (provinciaIds.length === 0) return of([]);
    const params = new HttpParams().set('provinciaIds', provinciaIds.join(','));
    return this.http.get<CatalogoItem[]>(`${this.profilePersonUrl}/localidades`, { params });
  }
}
